<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAlert } from 'dashboard/composables';

import CaptainAiActionProposalsAPI from 'dashboard/api/captain/aiActionProposals';
import CaptainAiActionLogsAPI from 'dashboard/api/captain/aiActionLogs';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const proposals = ref([]);
const logs = ref([]);
const meta = ref({ totalCount: 0, page: 1 });
const currentStatus = ref(route.query.status || 'pending');
const isFetching = ref(false);
const isFetchingLogs = ref(false);
const activeProposalId = ref(null);

const selectedAssistantId = computed(() => Number(route.params.assistantId));
const isEmpty = computed(
  () => !isFetching.value && proposals.value.length === 0
);

const statusOptions = computed(() => [
  { id: 'pending', label: t('CAPTAIN.AI_ACTIONS.STATUS.PENDING') },
  { id: 'approved', label: t('CAPTAIN.AI_ACTIONS.STATUS.APPROVED') },
  { id: 'rejected', label: t('CAPTAIN.AI_ACTIONS.STATUS.REJECTED') },
  { id: 'expired', label: t('CAPTAIN.AI_ACTIONS.STATUS.EXPIRED') },
  { id: 'failed', label: t('CAPTAIN.AI_ACTIONS.STATUS.FAILED') },
]);

const formatTime = timestamp => {
  if (!timestamp) return t('CAPTAIN.AI_ACTIONS.UNKNOWN_TIME');
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(timestamp * 1000));
};

const displayTool = toolId => {
  if (toolId === 'add_contact_note') {
    return t('CAPTAIN.AI_ACTIONS.TOOLS.add_contact_note');
  }
  if (toolId === 'add_private_note') {
    return t('CAPTAIN.AI_ACTIONS.TOOLS.add_private_note');
  }
  if (toolId === 'add_label_to_conversation') {
    return t('CAPTAIN.AI_ACTIONS.TOOLS.add_label_to_conversation');
  }
  if (toolId === 'update_priority') {
    return t('CAPTAIN.AI_ACTIONS.TOOLS.update_priority');
  }
  if (toolId === 'resolve_conversation') {
    return t('CAPTAIN.AI_ACTIONS.TOOLS.resolve_conversation');
  }
  if (toolId === 'handoff') return t('CAPTAIN.AI_ACTIONS.TOOLS.handoff');
  if (toolId === 'faq_lookup') {
    return t('CAPTAIN.AI_ACTIONS.TOOLS.faq_lookup');
  }
  if (toolId === 'send_public_reply') {
    return t('CAPTAIN.AI_ACTIONS.TOOLS.send_public_reply');
  }
  if (toolId === 'assign_conversation') {
    return t('CAPTAIN.AI_ACTIONS.TOOLS.assign_conversation');
  }
  if (toolId === 'update_custom_attributes') {
    return t('CAPTAIN.AI_ACTIONS.TOOLS.update_custom_attributes');
  }
  if (toolId === 'classify_conversation') {
    return t('CAPTAIN.AI_ACTIONS.TOOLS.classify_conversation');
  }
  return toolId;
};

const displayLogSummary = log => {
  return [displayTool(log.tool_id), log.status, log.decision].join(' · ');
};

const displayPreview = proposal => {
  const preview = proposal.preview || {};
  const args = proposal.arguments || {};
  return (
    preview.response ||
    preview.note ||
    args.response ||
    args.note ||
    args.label_name ||
    args.priority ||
    args.reason ||
    t('CAPTAIN.AI_ACTIONS.NO_PREVIEW')
  );
};

const updateURL = page => {
  router.replace({
    query: {
      page,
      status: currentStatus.value,
    },
  });
};

const fetchProposals = async (page = 1) => {
  isFetching.value = true;
  try {
    const { data } = await CaptainAiActionProposalsAPI.get({
      page,
      status: currentStatus.value,
      assistantId: selectedAssistantId.value,
    });
    proposals.value = data.payload || [];
    meta.value = {
      totalCount: Number(data.meta?.total_count || 0),
      page: Number(data.meta?.page || page),
    };
    updateURL(meta.value.page);
  } catch (error) {
    useAlert(t('CAPTAIN.AI_ACTIONS.API.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const fetchLogs = async () => {
  isFetchingLogs.value = true;
  try {
    const { data } = await CaptainAiActionLogsAPI.get({
      page: 1,
      assistantId: selectedAssistantId.value,
    });
    logs.value = data.payload || [];
  } catch (error) {
    logs.value = [];
  } finally {
    isFetchingLogs.value = false;
  }
};

const refresh = async (page = meta.value.page || 1) => {
  await fetchProposals(page);
  await fetchLogs();
};

const approveProposal = async proposal => {
  activeProposalId.value = proposal.id;
  try {
    await CaptainAiActionProposalsAPI.approve(proposal.id);
    useAlert(t('CAPTAIN.AI_ACTIONS.API.APPROVE_SUCCESS'));
    await refresh();
  } catch (error) {
    useAlert(t('CAPTAIN.AI_ACTIONS.API.APPROVE_ERROR'));
  } finally {
    activeProposalId.value = null;
  }
};

const rejectProposal = async proposal => {
  activeProposalId.value = proposal.id;
  try {
    await CaptainAiActionProposalsAPI.reject(
      proposal.id,
      t('CAPTAIN.AI_ACTIONS.REJECTED_FROM_DASHBOARD')
    );
    useAlert(t('CAPTAIN.AI_ACTIONS.API.REJECT_SUCCESS'));
    await refresh();
  } catch (error) {
    useAlert(t('CAPTAIN.AI_ACTIONS.API.REJECT_ERROR'));
  } finally {
    activeProposalId.value = null;
  }
};

const onStatusChange = status => {
  currentStatus.value = status;
  fetchProposals(1);
};

const onPageChange = page => {
  fetchProposals(page);
};

onMounted(() => {
  const page = parseInt(route.query.page, 10) || 1;
  refresh(page);
});
</script>

<template>
  <PageLayout
    :total-count="meta.totalCount"
    :current-page="meta.page"
    :header-title="$t('CAPTAIN.AI_ACTIONS.HEADER')"
    :is-fetching="isFetching"
    :is-empty="isEmpty"
    :show-pagination-footer="!isFetching && !!proposals.length"
    :feature-flag="FEATURE_FLAGS.CAPTAIN"
    @update:current-page="onPageChange"
  >
    <template #controls>
      <div class="flex flex-wrap items-center gap-2 mb-4">
        <Button
          v-for="statusOption in statusOptions"
          :key="statusOption.id"
          :label="statusOption.label"
          :variant="currentStatus === statusOption.id ? 'solid' : 'faded'"
          color="slate"
          size="sm"
          @click="onStatusChange(statusOption.id)"
        />
      </div>
    </template>

    <template #emptyState>
      <div
        class="flex flex-col items-center justify-center h-full py-16 text-center"
      >
        <div class="mb-3 text-4xl text-n-slate-9">
          <Icon icon="i-woot-captain" />
        </div>
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('CAPTAIN.AI_ACTIONS.EMPTY_STATE.TITLE') }}
        </h3>
        <p class="max-w-lg mt-2 text-sm text-n-slate-11">
          {{ $t('CAPTAIN.AI_ACTIONS.EMPTY_STATE.SUBTITLE') }}
        </p>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <article
          v-for="proposal in proposals"
          :key="proposal.id"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4 shadow-sm"
        >
          <div
            class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between"
          >
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ displayTool(proposal.tool_id) }}
                </span>
                <span
                  class="rounded-md bg-n-slate-3 px-2 py-0.5 text-xs text-n-slate-11"
                >
                  {{ proposal.status }}
                </span>
                <span
                  class="rounded-md bg-n-blue-3 px-2 py-0.5 text-xs text-n-blue-11"
                >
                  {{ proposal.mode }}
                </span>
              </div>
              <p class="mt-2 whitespace-pre-wrap text-sm text-n-slate-12">
                {{ displayPreview(proposal) }}
              </p>
              <p class="mt-2 text-xs text-n-slate-10">
                {{ proposal.reason }}
              </p>
              <div
                class="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-n-slate-10"
              >
                <span>
                  {{
                    $t('CAPTAIN.AI_ACTIONS.PROPOSED_AT', {
                      time: formatTime(proposal.proposed_at),
                    })
                  }}
                </span>
                <span v-if="proposal.conversation">
                  {{
                    $t('CAPTAIN.AI_ACTIONS.CONVERSATION', {
                      id: proposal.conversation.display_id,
                    })
                  }}
                </span>
                <span v-if="proposal.contact?.name">
                  {{
                    $t('CAPTAIN.AI_ACTIONS.CONTACT', {
                      name: proposal.contact.name,
                    })
                  }}
                </span>
              </div>
            </div>

            <div
              v-if="proposal.status === 'pending'"
              class="flex shrink-0 gap-2"
            >
              <Button
                :label="$t('CAPTAIN.AI_ACTIONS.REJECT')"
                variant="faded"
                color="slate"
                size="sm"
                :is-loading="activeProposalId === proposal.id"
                @click="rejectProposal(proposal)"
              />
              <Button
                :label="$t('CAPTAIN.AI_ACTIONS.APPROVE')"
                color="blue"
                size="sm"
                :is-loading="activeProposalId === proposal.id"
                @click="approveProposal(proposal)"
              />
            </div>
          </div>
        </article>
      </div>

      <section class="mt-8 rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <div class="mb-3 flex items-center justify-between">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ $t('CAPTAIN.AI_ACTIONS.RECENT_LOGS') }}
          </h3>
          <Spinner v-if="isFetchingLogs" class="size-4" />
        </div>
        <div v-if="!logs.length" class="text-sm text-n-slate-10">
          {{ $t('CAPTAIN.AI_ACTIONS.NO_LOGS') }}
        </div>
        <ul v-else class="divide-y divide-n-weak">
          <li
            v-for="log in logs.slice(0, 5)"
            :key="log.id"
            class="flex items-center justify-between gap-4 py-2 text-sm"
          >
            <span class="min-w-0 truncate text-n-slate-11">
              {{ displayLogSummary(log) }}
            </span>
            <span class="shrink-0 text-xs text-n-slate-9">
              {{ formatTime(log.created_at) }}
            </span>
          </li>
        </ul>
      </section>
    </template>
  </PageLayout>
</template>
